import TypedLine from './TypedLine';
import tw from 'twin.macro';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import TextEditor from 'components/form/text-editor/TextEditor';
import { MAX_INPUT_LENGTH } from './VacancyPreviewer';
import useDefocusHandler from 'hooks/defocus';
import { wait } from 'utils/baseHelpers';
import { FloatingButton } from './VacancyEditorColor';
import ColorPicker from 'components/form/ColorPicker';
import useVacancyContext from 'hooks/context/vacancy-context';
import { VacancyCopyKeyname } from 'types/vacancyTypes';
import { PencilSimple, TextAUnderline } from '@phosphor-icons/react';
import { getInitialPrompt } from 'utils/vacancy-generator/vacancyHelpers';
import { VacancyAIPromptPayload } from 'utils/vacancy-generator/prompts';
import useNavigationContext from 'hooks/context/nav-context';
import { getFormattedText } from 'utils/openAiHelpers';
import { STOP_AI_ANSWER } from 'utils/aiAnswerInProgress';
import { convertToRaw, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { createStateFromDefaultValue } from 'utils/templateHelpers';

/**
 * The words as the editor itself would write them back. Opening a text that
 * was stored a little differently (empty, or without the closing line break)
 * makes the editor report this form on the first caret move, with nothing
 * typed. That is not a change (review round 4, A9).
 */
const asTheEditorWritesIt = (html: string): string | null => {
  try {
    const state = html
      ? createStateFromDefaultValue(html)
      : EditorState.createEmpty();
    return draftToHtml(convertToRaw(state.getCurrentContent()));
  } catch {
    return null;
  }
};

const VacancyContentInput = ({
  id,
  keyName,
  title,
  text,
  cta,
  vacancyAIPayload,
  contentPrefix,
  onTitleChange,
  onTextChange,
  onCtaChange,
}: {
  id?: string;
  keyName?: VacancyCopyKeyname;
  title?: string;
  text?: string;
  cta?: string;
  vacancyAIPayload?: VacancyAIPromptPayload;
  contentPrefix?: React.ReactNode;
  onTitleChange: (value: string | null) => void;
  onTextChange: (value: string) => void;
  onCtaChange?: (value: string) => void;
}) => {
  const { generatingFields, copyData, setCopyData } = useVacancyContext();

  const { activeVacancy } = useNavigationContext();

  const TextEditorRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  /** The last text this field itself reported, to recognise its own echo. */
  const lastReportedRef = useRef<string | null>(null);

  /**
   * The parent refuses a heading longer than MAX_INPUT_LENGTH and keeps the
   * PREVIOUS value. Now that the field reports on every keystroke, that refusal
   * arrives back as "your whole heading is the old one", the field is rewritten
   * and the caret jumps -- on every further keystroke. So the limit is applied
   * here, where the extra character can simply be refused instead.
   */
  const reportHeading = (value: string | null) => {
    const within = (value ?? '').slice(0, MAX_INPUT_LENGTH);

    if (within !== (value ?? '') && headingRef.current) {
      headingRef.current.textContent = within;
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(headingRef.current);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }

    lastReportedRef.current = within;
    onTitleChange(within);
  };

  /*
   * The heading is deliberately NOT a React-controlled field. Re-rendering its
   * children on every keystroke puts the caret back at the start, which is the
   * reason it used to report only on blur. The DOM owns the text while the
   * person is in it; this puts the stored value back whenever they are not.
   */
  /*
   * Puts a value that came from OUTSIDE this field back on screen: an undo, a
   * redo, a reload, the AI helper. Our own keystrokes arrive back here as props
   * too, and rewriting on those would throw the caret to the start -- which is
   * why this field used to report only on blur. The value last reported from
   * this field is remembered, so its own echo is ignored and everything else is
   * obeyed, focused or not.
   */
  useEffect(() => {
    const heading = headingRef.current;
    if (!heading) return;

    const incoming = title ?? '';
    if (heading.textContent === incoming) return;
    if (lastReportedRef.current === incoming) return;

    heading.textContent = incoming;
    // Now the echo to ignore is THIS value, not what was typed before the undo,
    // or a redo straight afterwards would be mistaken for our own keystroke.
    lastReportedRef.current = incoming;

    if (document.activeElement === heading) {
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(heading);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }, [title]);
  const ColorRef = useRef<HTMLDivElement>(null);

  const [isEditing, setIsEditing] = useState(false);

  /*
   * The body text, like the heading, has to obey a value that comes from
   * OUTSIDE while the person is in it: Ctrl/Cmd+Z inside the box now steps the
   * screen back (Steven, 3 Oct 2026), and the editor only reads its words once,
   * when it opens. Left alone it would keep showing the undone words and hand
   * them straight back on the next keystroke.
   *
   * So the text this box itself last reported is remembered. Its own echo is
   * ignored; anything else reopens the editor on the new words.
   */
  const lastReportedTextRef = useRef<string | null>(null);
  /** The words the editor was opened on, and how it writes those same words. */
  const openedOnRef = useRef<{ raw: string; rewritten: string | null } | null>(
    null
  );
  const roundRef = useRef(0);
  const [editorRound, setEditorRound] = useState(0);
  /** The one rebuild that takes the caret: the box had it when it was rebuilt. */
  const [focusRound, setFocusRound] = useState(-1);

  /** Is an AI answer arriving in this box right now? See TextEditor. */
  const answerIsArriving = () =>
    !!TextEditorRef.current?.querySelector('[data-ai-busy]');

  const openOn = (words: string) => {
    lastReportedTextRef.current = words;
    openedOnRef.current = { raw: words, rewritten: asTheEditorWritesIt(words) };
  };

  const reportText = (value: string) => {
    const openedOn = openedOnRef.current;
    const nothingTypedYet =
      !!openedOn &&
      lastReportedTextRef.current === openedOn.raw &&
      value === openedOn.rewritten;
    if (nothingTypedYet) return;

    lastReportedTextRef.current = value;
    onTextChange(value);

    // The one report made while an answer was arriving IS the answer. The
    // editor will write those words a little differently on the first caret
    // move; that is not a change either (review round 5, D11).
    if (answerIsArriving()) openOn(value);
  };

  useEffect(() => {
    if (!isEditing) {
      lastReportedTextRef.current = null;
      openedOnRef.current = null;
      setFocusRound(-1);
      return;
    }
    const incoming = text ?? '';
    if (lastReportedTextRef.current === null) {
      openOn(incoming);
      return;
    }
    if (lastReportedTextRef.current === incoming) return;

    /*
     * Rebuilt on the new words. It takes the caret only if the box had it: a
     * person who tabbed to the Undo arrow keeps the caret on the arrow, and a
     * later opening of the box does not grab it either (review round 4, A10).
     */
    const hadCaret =
      !!TextEditorRef.current &&
      TextEditorRef.current.contains(document.activeElement);
    openOn(incoming);
    roundRef.current += 1;
    setEditorRound(roundRef.current);
    setFocusRound(hadCaret ? roundRef.current : -1);
  }, [text, isEditing]);
  const [showTxtColors, setShowTxtColors] = useState(false);

  // Asked from outside (the Undo/Redo arrows, Ctrl/Cmd+Z) to stop the answer
  // arriving in this box: closing the box is what stops it.
  useEffect(() => {
    const stop = () => {
      if (TextEditorRef.current?.querySelector('[data-ai-busy]'))
        setIsEditing(false);
    };
    window.addEventListener(STOP_AI_ANSWER, stop);
    return () => window.removeEventListener(STOP_AI_ANSWER, stop);
  }, []);

  // TODO: revise
  useDefocusHandler(TextEditorRef, (event) => {
    // Also while an AI answer is arriving: closing the box stops the answer
    // and says so (Steven, 3 Oct 2026). See TextEditorAIPlugin.
    // The Undo and Redo arrows are not "somewhere else": the box stays open.
    // While an answer is arriving they stop it themselves, through
    // STOP_AI_ANSWER above, and do nothing else on that press.
    if (
      (event?.target as HTMLElement | null)?.closest?.('[data-keeps-editing]')
    )
      return;
    wait(0.05).then(() => setIsEditing(false)); // To prevent the onChange event of the TextEditor to not be triggered
  });
  useDefocusHandler(ColorRef, () => setShowTxtColors(false));

  const isGenerating = useMemo(
    () => !!keyName && generatingFields.includes(keyName),
    [generatingFields, keyName]
  );

  useEffect(() => {
    if (isGenerating && isEditing) setIsEditing(false);
  }, [isEditing, isGenerating]);

  const handleColorWrapperClick = (element: HTMLDivElement) => {
    if (element.closest('#txtColor') === null) setShowTxtColors(!showTxtColors);
  };

  const getSuggestedAction = () => {
    switch (keyName) {
      case 'vac_text_todo':
        return [
          {
            icon: <PencilSimple weight="fill" />,
            prompt: 'Write about daily tasks & job responsibilities',
            instantPrompt: true,
            highlighted: true,
          },
        ];
      case 'vac_text_expectations':
        return [
          {
            icon: <PencilSimple weight="fill" />,
            prompt: 'Write about required experience, education, skills',
            instantPrompt: true,
            highlighted: true,
          },
        ];
      case 'vac_text_offer':
        return [
          {
            icon: <PencilSimple weight="fill" />,
            prompt: 'Write about the offer, benefits & perks',
            instantPrompt: true,
            highlighted: true,
          },
        ];
      default:
        return [];
    }
  };

  const onFinished = async (value: string, thread: string) => {
    if (!activeVacancy) {
      console.error('No vacancy set');
      return;
    }
    return getFormattedText(value, activeVacancy, keyName ?? 'default', thread);
  };

  return (
    <>
      <article id={id} className="card">
        <section>
          <div tw="flex">
            <h2
              ref={headingRef}
              data-testid={`section-heading-${keyName ?? 'vacancy_copy'}`}
              className="section-heading highlight-white"
              data-page-undo
              contentEditable="true"
              suppressContentEditableWarning={true}
              /*
               * Auto-save (scene 1): the heading has to report while it is being
               * typed, not only when the person clicks away, or the work is kept
               * far later than the two seconds the description promises.
               */
              /*
               * Refuse the character BEFORE it lands. Trimming afterwards
               * deletes the far end of the heading instead of the character
               * just typed, and throws the caret to the end of the line.
               */
              onBeforeInput={(e) => {
                const heading = e.currentTarget;
                const isInsert = !(
                  e.nativeEvent as InputEvent
                ).inputType?.startsWith('delete');
                /*
                 * Typing OVER a selection replaces it, so the selected
                 * characters do not count against the limit. Without this a
                 * heading at its limit refused all typing, also with its
                 * whole text selected: it could only be shortened with
                 * Backspace (found 4 Oct 2026, when a check could not type
                 * over a full heading).
                 */
                const selection = window.getSelection();
                const selected =
                  selection &&
                  selection.rangeCount > 0 &&
                  heading.contains(selection.anchorNode) &&
                  heading.contains(selection.focusNode)
                    ? selection.toString().length
                    : 0;
                if (
                  isInsert &&
                  (heading.textContent ?? '').length - selected >=
                    MAX_INPUT_LENGTH
                ) {
                  e.preventDefault();
                }
              }}
              onInput={(e) => reportHeading(e.currentTarget.textContent)}
              onBlur={(e) => reportHeading(e.currentTarget.textContent)}
              onPaste={(e) => {
                /*
                 * Paste INTO the heading at the caret, within the limit.
                 *
                 * The old handler cancelled the paste and then reported the
                 * pasted text as the WHOLE heading, so pasting a word replaced
                 * everything. Harmless while a Save button stood between that
                 * and the database; with auto-save it is stored two seconds
                 * later by itself.
                 */
                e.preventDefault();

                const heading = e.currentTarget;
                const pasted = e.clipboardData.getData('text/plain');
                const selection = window.getSelection();
                const range = selection?.rangeCount
                  ? selection.getRangeAt(0)
                  : null;

                const current = heading.textContent ?? '';
                const selected = range ? range.toString().length : 0;
                const room = MAX_INPUT_LENGTH - (current.length - selected);
                const text = pasted
                  .replace(/\s+/g, ' ')
                  .slice(0, Math.max(0, room));

                if (range && heading.contains(range.commonAncestorContainer)) {
                  range.deleteContents();
                  const node = document.createTextNode(text);
                  range.insertNode(node);
                  range.setStartAfter(node);
                  range.collapse(true);
                  selection?.removeAllRanges();
                  selection?.addRange(range);
                } else {
                  heading.textContent = (current + text).slice(
                    0,
                    MAX_INPUT_LENGTH
                  );
                }

                reportHeading(heading.textContent);
              }}
            ></h2>
            <FloatingButton
              tw="absolute top-0 right-0 -mt-4 sm:-mr-4"
              loading={isGenerating}
              onClick={(e) => {
                if (!isGenerating)
                  handleColorWrapperClick(e.target as HTMLDivElement);
              }}
              ref={ColorRef}
            >
              <TextAUnderline weight="bold" tw="text-xl" />
              {showTxtColors && (
                <ColorPicker
                  id="txtColor"
                  onChange={(color) =>
                    setCopyData({
                      ...copyData!,
                      tertiary_color: color,
                    })
                  }
                  value={copyData?.tertiary_color}
                  variant={'barebones'}
                />
              )}
            </FloatingButton>
          </div>

          {contentPrefix}

          {isEditing ? (
            <div tw="my-6" ref={TextEditorRef} data-page-undo>
              <TextEditor
                key={editorRound}
                hasFocus={focusRound === editorRound}
                aiReportsWhenDone
                identifier={keyName ?? 'vacancy_copy'}
                defaultValue={text ?? ''}
                onChange={reportText}
                aiEnabled={!!vacancyAIPayload}
                // TODO: add location
                initialPrompt={
                  vacancyAIPayload
                    ? getInitialPrompt(keyName ?? 'default', vacancyAIPayload)
                    : undefined
                }
                onAIGenFinished={onFinished}
                aiSuggestedActions={[...getSuggestedAction()]}
              />
            </div>
          ) : (
            <div
              css={[tw`min-h-[7rem]`, isGenerating && tw`cursor-wait`]}
              className={`${!isGenerating && 'highlight-white'}`}
              dangerouslySetInnerHTML={{
                __html: text ?? '',
              }}
              onClick={() => setIsEditing(!isGenerating)}
            ></div>
          )}
        </section>
      </article>

      {cta && (
        <section className="form-link">
          <TypedLine
            tag="a"
            className="highlight-white"
            href="?"
            data-testid="job-button-text"
            value={cta}
            onWords={(words) => onCtaChange?.(words)}
          />
        </section>
      )}
    </>
  );
};

export default VacancyContentInput;
